/**
 * semantiker.js
 * Description: accepts valid parse tree and evaluate if the program is semantically correct
 */

/* eslint-disable default-case */

import {
  isBoolean,
  isNumberLiteral,
  isIdentifierToken,
  isUnaryOperatorToken,
  isBinaryBooleanOperatorToken,
  isInfOperatorToken,
  isArithmeticOperatorToken,
  isLiteralToken,
  generateError,
  getSymbol,
  properType,
  verifyValueType,
  XOR,
} from "./helpers.js";

// Evaluates each statement from the syntax tree
// INPUT: syntaxTree generated by the lexer
// OUTPUT: final symbolTable
export function semantiker(syntaxTree) {
  let symbolTable = {
    IT: null,
  };

  let outputMessages = [];

  for (let i = 1; i < syntaxTree.length - 1; i++) {
    evaluateStatement(symbolTable, syntaxTree[i], outputMessages);
  }

  return { symbolTable, outputMessages };
}

export function evaluateStatement(symbolTable, statement, outputMessages) {
  switch (statement.type) {
    case "VAR_DEC":
      addToSymbolTable(symbolTable, statement);
      break;
    case "IDENT":
      updateSymbolTable(symbolTable, statement);
      break;
    case "INPUT":
      updateSymbolTableByInput(symbolTable, statement);
      break;
    case "OUTPUT":
      outputMessages.push(output(symbolTable, statement));
      break;
    case "IF_START":
      evaluateIfElse(symbolTable, statement, outputMessages);
      break;
    case "SWITCH_START":
      evaluateSwitchBlock(symbolTable, statement, outputMessages);
      break;
    case "SWITCH_BREAK":
      return true;

    // For expressions
    default:
      updateItVariable(symbolTable, statement);
  }
}

// Adds a declared variable to the symbol table
export function addToSymbolTable(symbolTable, iHasAToken) {
  let varToken = iHasAToken.value[0];
  let valueToken = iHasAToken.value[1];

  // If the variable was given a value -> evaluate
  // Else,  set its value to null
  if (valueToken)
    symbolTable[varToken.token] = evaluateExpression(symbolTable, valueToken);
  else symbolTable[varToken.token] = null;
}

// Updates the value of an identifier in our symbol table
export function updateSymbolTable(symbolTable, varidentToken) {
  let varident = varidentToken.token;

  // If variable is not in symbol table, throw error
  if (varident in symbolTable)
    symbolTable[varident] = evaluateExpression(
      symbolTable,
      varidentToken.value[0]
    );
  else
    throw new Error(generateError(`${varident} not declared.`, varidentToken));
}

// Updates the value of an identifier in our symbol table after GIMMEH
export function updateSymbolTableByInput(symbolTable, gimmehToken) {
  let varidentToken = gimmehToken.value[0];
  let varident = varidentToken.token;

  // If variable is not in symbol table, throw error
  //if(!symbolTable[varident])
  if (varident in symbolTable) {
    let value = prompt(`Gimmeh a value for ${varident}: `);
    symbolTable[varident] = isNaN(value) ? value : Number(value);
  } else
    throw new Error(
      generateError(
        `Can't give ${varident} a value if it is not declared.`,
        varidentToken
      )
    );
}

// Updates the value of the IT variable in our symbol table
export function updateItVariable(symbolTable, valueToken) {
  let value = evaluateExpression(symbolTable, valueToken);
  symbolTable.IT = value;
}

// Evaluate visible statement
// Concatenates the evaluated VISIBLE args then returns the message
export function output(symbolTable, visibleToken) {
  let visibleArray = visibleToken.value;

  let outputMessage = "";
  let newMessage = "";

  for (let i in visibleArray) {
    if (isIdentifierToken(visibleArray[i]))
      newMessage = getSymbol(symbolTable, visibleArray[i]);
    else newMessage = evaluateExpression(symbolTable, visibleArray[i]);

    if (newMessage === true) newMessage = "WIN";
    else if (newMessage === false) newMessage = "FAIL";

    outputMessage = outputMessage.concat(newMessage);
  }

  return outputMessage;
}

// Evaluates an if-else block
// Checks the value of IT to assign which statement block to execute
// Then executes that block
function evaluateIfElse(symbolTable, ifBlockToken, outputMessages) {
  let trueBlock = ifBlockToken.value[0];
  let falseBlock = ifBlockToken.value[1];
  let evalBlock = symbolTable.IT === true ? trueBlock : falseBlock;

  for (let i = 0; i < evalBlock.value.length; i++) {
    evaluateStatement(symbolTable, evalBlock.value[i], outputMessages);
  }
}

function evaluateSwitchBlock(symbolTable, wtfToken, outputMessages) {
  let cases = wtfToken.value;
  let continueNextCase = false;

  // Run cases
  for (let i = 0; i < cases.length - 1; i++) {
    let caseLiteral = cases[i].value[0];
    let caseStatements =
      cases[i].type === "SWITCH_DEF" ? cases[i].value : caseLiteral.value;

    if (
      symbolTable.IT === properType(caseLiteral) ||
      cases[i].type === "SWITCH_DEF" ||
      continueNextCase
    ) {
      continueNextCase = true;

      for (let j = 0; j < caseStatements.length; j++) {
        let gtfo = evaluateStatement(
          symbolTable,
          caseStatements[j],
          outputMessages
        );
        if (gtfo) return;
      }
    }
  }
}

// Evaluate an expression (mathematical/boolean)
function evaluateExpression(symbolTable, valueToken) {
  if (isLiteralToken(valueToken)) return properType(valueToken);
  else if (isIdentifierToken(valueToken))
    return getSymbol(symbolTable, valueToken);
  else if (isArithmeticOperatorToken(valueToken))
    return evaluateMathExpr(symbolTable, valueToken);
  else if (isBoolean(valueToken))
    return evaluateBoolExpr(symbolTable, valueToken);
  else if (isInfOperatorToken(valueToken))
    return evaluateAnyAllExpr(symbolTable, valueToken);
}

export function evaluateMathExpr(symbolTable, valueToken) {
  // Converts literals and the value of varidents to Number
  if (isNumberLiteral(valueToken)) return Number(valueToken.token);
  else if (isIdentifierToken(valueToken))
    return verifyValueType(symbolTable, valueToken, "number");
  // Performs arithmetic operations on evaluated Numbers
  else if (isArithmeticOperatorToken(valueToken)) {
    let first = valueToken.value[0];
    let second = valueToken.value[1];

    switch (valueToken.type) {
      case "OP_ADD":
        return (
          evaluateMathExpr(symbolTable, first) +
          evaluateMathExpr(symbolTable, second)
        );
      case "OP_SUB":
        return (
          evaluateMathExpr(symbolTable, first) -
          evaluateMathExpr(symbolTable, second)
        );
      case "OP_MUL":
        return (
          evaluateMathExpr(symbolTable, first) *
          evaluateMathExpr(symbolTable, second)
        );
      case "OP_DIV":
        return (
          evaluateMathExpr(symbolTable, first) /
          evaluateMathExpr(symbolTable, second)
        );
      case "OP_MOD":
        return (
          evaluateMathExpr(symbolTable, first) %
          evaluateMathExpr(symbolTable, second)
        );
      case "OP_MAX":
        return Math.max(
          evaluateMathExpr(symbolTable, first),
          evaluateMathExpr(symbolTable, second)
        );
      case "OP_MIN":
        return Math.min(
          evaluateMathExpr(symbolTable, first),
          evaluateMathExpr(symbolTable, second)
        );
    }

    // Throw error if value is not a number or not a mathematical expression
  } else
    throw new Error(
      generateError(
        `Expected type: INT/FLOAT. Got ${valueToken.type}`,
        valueToken
      )
    );
}

function evaluateBoolExpr(symbolTable, valueToken) {
  // Converts literals and the value of varidents to Boolean
  if (valueToken.type === "BOOL")
    return valueToken.token === "WIN" ? true : false;
  else if (isIdentifierToken(valueToken))
    return verifyValueType(symbolTable, valueToken, "boolean");
  // Performs arithmetic operations on evaluated Booleans
  // [1] NOT Operator
  else if (isUnaryOperatorToken(valueToken))
    return !evaluateBoolExpr(symbolTable, valueToken.value[0]);
  // [2] Binary Boolean Operators
  else if (isBinaryBooleanOperatorToken(valueToken)) {
    let first = valueToken.value[0];
    let second = valueToken.value[1];

    switch (valueToken.type) {
      case "OP_AND":
        return (
          evaluateBoolExpr(symbolTable, first) &&
          evaluateBoolExpr(symbolTable, second)
        );
      case "OP_OR":
        return (
          evaluateBoolExpr(symbolTable, first) ||
          evaluateBoolExpr(symbolTable, second)
        );
      case "OP_XOR":
        return XOR(
          evaluateBoolExpr(symbolTable, first),
          evaluateBoolExpr(symbolTable, second)
        );
      case "OP_EQU":
        return (
          evaluateExpression(symbolTable, first) ===
          evaluateExpression(symbolTable, second)
        );
      case "OP_NEQ":
        return (
          evaluateExpression(symbolTable, first) !==
          evaluateExpression(symbolTable, second)
        );
    }

    // Throw error if value is not a boolean expression
  } else
    throw new Error(
      generateError(`Expected type: BOOL. Got ${valueToken.type}`, valueToken)
    );
}

export function evaluateAnyAllExpr(symbolTable, valueToken) {
  let operands = valueToken.value;

  // Return true if ANY of the evaluated expressions return true
  if (valueToken.type === "OP_ANY") {
    for (let i in operands) {
      let currentValue = evaluateBoolExpr(symbolTable, operands[i]);
      if (currentValue === true) return true;
    }
    return false;

    // Return true if ALL of the evaluated expressions return true
  } else if (valueToken.type === "OP_ALL") {
    for (let i in operands) {
      let currentValue = evaluateBoolExpr(symbolTable, operands[i]);
      if (currentValue !== true) return false;
    }
    return true;
  }
}
